import React, { useState, useRef, useEffect, useMemo, useCallback } from "react"
import { chevronDown } from "../hooks/icons"
import { useSearchspringContext } from "@app/scripts/providers/searchspring"
import RangeSlider from "react-range-slider-input"
import { currentCountryCurrency, sizeLabel } from "@app/scripts/lib/utils"
import { fabricLabels } from "@app/scripts/lib/fabric"

const SIZE_FACET_FIELD = "ss_filter_sizes"
const FABRIC_FACET_FIELD = "tags_x_fabric"

// Canonical display order for merged size pills: one-size, alpha run,
// numerics ascending, then anything else in the order Searchspring returned.
// "ONE SIZE" and "OS" both appear because the label is what gets ranked, and
// whether OS has been mapped through the sizing metaobject yet is data, not code.
const ALPHA_SIZE_ORDER = [
  "ONE SIZE",
  "OS",
  "XXS",
  "XS",
  "S",
  "M",
  "L",
  "XL",
  "XXL",
]

// US mens footwear, e.g. M-4.5. Ranked past the EU run so it does not interleave
// with the US womens numbers it shares digits with.
const MENS_SHOE = /^M-(\d+(?:\.\d+)?)$/

const sizeRank = (label: string) => {
  const upper = label.toUpperCase()
  const alphaIndex = ALPHA_SIZE_ORDER.indexOf(upper)
  if (alphaIndex !== -1) return alphaIndex
  const mens = MENS_SHOE.exec(upper)
  if (mens) return 200 + Number(mens[1])
  const numeric = Number(label)
  if (!Number.isNaN(numeric)) return 100 + numeric
  return 1000
}

// Size grouping (#1809). Denim and Footwear are derivable from the size value
// alone, verified against all 538 indexed products: the denim run in the
// catalogue today is waist 22-32 and only ever sits on jeans, while EU 36-42,
// US womens 5-7.5 and the US mens M-x run only ever sit on shoes. Everything
// else is Ready to Wear, including accessories: belts share S/M/L with dresses,
// so an accessories group cannot be derived from the size value and would hold
// 3 belts and 6 tops. Client agreed to that on 27 Aug 2026.
//
// The denim test deliberately reaches to 34 rather than the 32 the catalogue
// stops at, so a new waist lands in Denim instead of silently falling through to
// Ready to Wear. Nothing between 33 and 34 exists yet; widen further if the
// range ever grows. Legacy numerics 0-4 fall through to Ready to Wear, which
// also keeps the layout honest if the sizing metaobject ever fails to resolve
// them to XXS-XL.
const SIZE_GROUPS = ["Ready to Wear", "Denim", "Footwear"] as const
type SizeGroup = (typeof SIZE_GROUPS)[number]

const sizeGroup = (label: string): SizeGroup => {
  if (MENS_SHOE.test(label.toUpperCase())) return "Footwear"
  const numeric = Number(label)
  if (Number.isNaN(numeric)) return "Ready to Wear"
  if (numeric >= 5 && numeric <= 7.5) return "Footwear"
  if (numeric >= 36 && numeric <= 42) return "Footwear"
  if (numeric >= 22 && numeric <= 34) return "Denim"
  return "Ready to Wear"
}

type FilterValue = {
  value?: string
  label?: string
  count?: number
  field?: string
  active?: boolean
}

type FilterData = {
  field: string
  label: string
  values: FilterValue[]
  range?: [number, number]
  active?: [number, number]
  activeSort?: string
}

type Props = {
  data: FilterData
  handleToggleFilter: (value: string) => void
  activeFilters: Set<string>
}

type FacetGroup = { keys: string[]; activeKeys: string[]; count: number }

// Merges raw facet values into display pills. `labelsFor` returns every label a
// raw value belongs to: exactly one for sizes, several for a fabric blend, none
// at all for a fabric the client excluded.
//
// handleToggleFilter flips each key it is handed, so a pill must hand it only
// the keys that actually need flipping. That distinction matters because fabric
// groups OVERLAP where size groups do not: "70% cotton 30% linen" belongs to
// both Cotton and Linen. Handing over the whole key set would flip the shared
// ones back off, so selecting Cotton while Linen was active used to silently
// tear keys out of Linen and leave Cotton reading unchecked.
//
// So: turning on sends only the keys not already active, and turning off sends
// only the active keys that no other selected pill still needs. Size groups are
// disjoint and always wholly on or off, so this is identical to the previous
// behaviour for them.
//
// The joined key list relies on same-field filters ORing, which they do:
// searchspring.ts appends one `filter.<field>=<value>` param per key.
// `sortKey` returns a number for a positional run like sizes, or a string for an
// alphabetical list like fabrics.
const mergeFacetValues = (
  data: FilterData,
  labelsFor: (rawValue: string) => string[],
  sortKey: (label: string, group: FacetGroup) => number | string
) => {
  const groups = new Map<string, FacetGroup>()

  data.values.forEach((item) => {
    const rawValue = String(item.value ?? item.label ?? "")
    const key = `${data.field}:${rawValue}`

    labelsFor(rawValue).forEach((label) => {
      const group = groups.get(label) || { keys: [], activeKeys: [], count: 0 }
      group.keys.push(key)
      if (item?.active) group.activeKeys.push(key)
      group.count += item?.count ?? 0
      groups.set(label, group)
    })
  })

  // A pill reads as selected only when every one of its keys is active. "Any key
  // active" would light up Cotton the moment Linen was picked, on the strength of
  // the one blend they share.
  const entries = Array.from(groups.entries())
  const isChecked = ([, group]: [string, FacetGroup]) =>
    group.keys.length > 0 && group.activeKeys.length === group.keys.length

  return entries
    .sort(([aLabel, aGroup], [bLabel, bGroup]) => {
      const a = sortKey(aLabel, aGroup)
      const b = sortKey(bLabel, bGroup)
      return typeof a === "string" && typeof b === "string"
        ? a.localeCompare(b)
        : Number(a) - Number(b)
    })
    .map((entry) => {
      const [label, group] = entry
      const checked = isChecked(entry)
      const heldElsewhere = new Set(
        entries
          .filter((other) => other[0] !== label && isChecked(other))
          .flatMap((other) => other[1].keys)
      )
      const toFlip = checked
        ? group.activeKeys.filter((key) => !heldElsewhere.has(key))
        : group.keys.filter((key) => !group.activeKeys.includes(key))

      return {
        label,
        value: toFlip.join(","),
        count: group.count,
        checked,
        disabled: false,
      }
    })
}

export function FitlerSortCollapsibleRow({
  data,
  handleToggleFilter,
  activeFilters,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const detailsRef = useRef<HTMLDetailsElement | null>(null)

  const { refine } = useSearchspringContext()
  const isSortBy = data?.field === "sort_by"
  const isPrice = data?.field === "ss_price"
  const isSize = data?.field === SIZE_FACET_FIELD
  const [stableRange, setStableRange] = useState<[number, number]>(
    data?.range || [0, 1000]
  )
  const [currentValue, setCurrentValue] = useState<[number, number]>(
    data?.active || data?.range || [0, 1000]
  )

  // Simple debounce for price filter
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedPriceFilter = useCallback(
    (newRange: [number, number]) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        handleToggleFilter(`ss_price:${newRange[0]}<>${newRange[1]}`)
      }, 500)
    },
    [handleToggleFilter]
  )

  const handleRangeChange = useCallback(
    (newRange: [number, number]) => {
      setCurrentValue(newRange)
      debouncedPriceFilter(newRange)
    },
    [debouncedPriceFilter]
  )

  useEffect(() => {
    if (data?.field !== "ss_price" || !data?.range) return
    setStableRange(data?.range)
  }, [])

  useEffect(() => {
    if (data?.field !== "ss_price") return
    setCurrentValue(data?.active || data?.range || [0, 1000])
  }, [data?.active, data?.range])

  useEffect(() => {
    const details = detailsRef.current
    const toggleOpen = () => setIsOpen(details?.open)

    if (details) {
      details.addEventListener("toggle", toggleOpen)
    }

    return () => {
      if (details) {
        details.removeEventListener("toggle", toggleOpen)
      }
    }
  }, [])

  const values = useMemo(() => {
    // Size facet: relabel legacy numeric values to alpha and merge values that
    // resolve to the same label into one pill (#1747), ordered by size run.
    if (data?.field === SIZE_FACET_FIELD) {
      return mergeFacetValues(data, (raw) => [sizeLabel(raw)], sizeRank)
    }

    // Material facet: map each raw x-fabric value to the approved fibres it
    // contains (#1809). One-to-many, so a blend lands in several pills, and
    // excluded fibres map to nothing and disappear. Ordered by count descending
    // to match the other list facets, which Searchspring sorts the same way.
    if (data?.field === FABRIC_FACET_FIELD) {
      return mergeFacetValues(
        data,
        fabricLabels,
        // Alphabetical, like the other text facets. Client asked for this on
        // 4 Sep 2026, replacing the count-descending order Searchspring uses.
        (label) => label
      )
    }

    const mapped =
      data.values.map((item) => {
        const value = item.value || item.label
        const valueWithKey = `${data.field || item.field}:${value}`

        const fetchedValue = false

        return {
          label: String(item.label).toLowerCase(),
          value: isSortBy ? (item.value ?? "") : valueWithKey,
          count: item?.count,
          checked: !!(item?.active || data?.activeSort === item?.label),
          disabled: !fetchedValue || false,
        }
      }) ?? []

    // Text facets read alphabetically (#1809, client request 4 Sep 2026).
    // Sort By keeps the order Searchspring returns, because those options are a
    // deliberate sequence rather than a list to look a value up in.
    return isSortBy
      ? mapped
      : [...mapped].sort((a, b) => a.label.localeCompare(b.label))
  }, [data, activeFilters, isSortBy])

  // Ready to Wear, Denim, Footwear. Groups with nothing in them are dropped so a
  // collection of jeans only shows Denim rather than two empty dropdowns.
  const sizeGroups = useMemo(() => {
    if (!isSize) return []
    const grouped = new Map<SizeGroup, typeof values>(
      SIZE_GROUPS.map((name) => [name, []])
    )
    values.forEach((value) => grouped.get(sizeGroup(value.label))?.push(value))
    return SIZE_GROUPS.map((name) => ({
      name,
      values: grouped.get(name) ?? [],
    })).filter((group) => group.values.length > 0)
  }, [isSize, values])

  // Hide the entire price filter accordion if currency is not AUD
  if (isPrice && currentCountryCurrency?.currencyCode !== "AUD") {
    return null
  }

  const renderValue = (value: (typeof values)[number], index: number) => (
    <div
      key={index}
      className="filter-sort-options--item"
      onClick={
        isSortBy
          ? () => refine("sort", value.value)
          : () => handleToggleFilter(value.value)
      }
    >
      <input
        type={isSortBy ? "radio" : "checkbox"}
        className={isSortBy ? "custom-radio" : "custom-check"}
        checked={value.checked}
      />
      {value.label && (
        <p className="filter-sort-option--title">{value.label}</p>
      )}
      {value.count && (
        <p className="filter-sort-option--count">({value.count})</p>
      )}
    </div>
  )

  return (
    <div className="product--accordion accordion">
      <details ref={detailsRef}>
        <summary className="accordion--title">
          {data?.label}
          <span className={`chevron-icon ${isOpen ? "open" : ""}`}>
            {chevronDown}
          </span>
        </summary>
        <div
          className={`${isSortBy ? "" : "collapsible__content"} accordion__content ${isSize ? "accordion__content--grouped" : ""}`}
        >
          {isPrice && (
            <div className="price-range-slider__container">
              <RangeSlider
                min={stableRange[0]}
                max={stableRange[1]}
                step={1}
                value={currentValue}
                onInput={handleRangeChange}
              />
              <div className="price-range-slider__prices">
                <p>${stableRange[0]}</p>
                <p>${stableRange[1]}</p>
              </div>
            </div>
          )}

          {isSize
            ? sizeGroups.map((group) => (
                <details
                  key={group.name}
                  className="filter-sort-size-group"
                  open={group.name === "Ready to Wear"}
                >
                  <summary className="filter-sort-size-group--title">
                    {group.name}
                    <span className="filter-sort-size-group--count">
                      {group.values.length}
                    </span>
                    <span className="chevron-icon">{chevronDown}</span>
                  </summary>
                  <div className="filter-sort-size-group--content">
                    {group.values.map(renderValue)}
                  </div>
                </details>
              ))
            : values.map(renderValue)}
        </div>
      </details>
    </div>
  )
}
